import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import SyncManager from '../sync/SyncManager';
import { resetLocalData, ensureAccount } from '../db/localdb';

// Identity that scopes the offline DB. Offline stores are per-business, so the
// business id is the natural key; fall back to the user id for safety.
const accountKeyOf = (user, business) => business?.id || user?.id || null;

// Last good session, so a signed-in user who opens the app with no signal
// still gets in (the pages that read the local DB keep working).
const SESSION_CACHE = 'shopbook_session';
const cacheSession = (d) => {
  try { localStorage.setItem(SESSION_CACHE, JSON.stringify(d)); } catch { /* storage full/blocked */ }
};
const cachedSession = () => {
  try { return JSON.parse(localStorage.getItem(SESSION_CACHE) || 'null'); } catch { return null; }
};

// Non-secret hint that this browser has a session, so visitors on the landing
// page don't trigger a pointless /auth/me call. The session itself is an
// httpOnly cookie that JavaScript can't read.
const SIGNED_IN = 'shopbook_signed_in';
const ME_TIMEOUT_MS = 8000;
// Where the token used to be kept, before the cookie switch.
const LEGACY_TOKEN = 'shopbook_token';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser]                   = useState(null);
  const [business, setBusiness]           = useState(null);
  const [parentCompany, setParentCompany] = useState(null);
  const [billing, setBilling]             = useState(null);
  const [legal, setLegal]                 = useState(null);
  const [loading, setLoading]             = useState(true);

  const apply = (d) => {
    setUser(d.user);
    setBusiness(d.business);
    setParentCompany(d.parentCompany);
    setBilling(d.billing || null);
    setLegal(d.legal || null);
  };

  const clearSession = () => {
    localStorage.removeItem(SIGNED_IN);
    localStorage.removeItem(LEGACY_TOKEN);
    localStorage.removeItem(SESSION_CACHE);
    setUser(null); setBusiness(null); setParentCompany(null); setBilling(null); setLegal(null);
  };

  const loadUser = useCallback(async () => {
    // One-time move for sessions from before the cookie switch: trade the old
    // stored token for the httpOnly cookie, then delete the stored copy.
    const legacy = localStorage.getItem(LEGACY_TOKEN);
    if (legacy) {
      try {
        await api.post('/auth/upgrade-session', null, { headers: { Authorization: `Bearer ${legacy}` } });
        localStorage.setItem(SIGNED_IN, '1');
      } catch { /* expired — the user signs in again */ }
      localStorage.removeItem(LEGACY_TOKEN);
    }
    if (!localStorage.getItem(SIGNED_IN)) { setLoading(false); return; }
    // No signal at all: open straight from the last known session instead of
    // waiting for a request that can't succeed.
    if (!navigator.onLine) {
      const cached = cachedSession();
      if (cached) { apply(cached); SyncManager.start(); }
      setLoading(false);
      return;
    }
    try {
      // Bounded, so a very weak signal falls back to the cached session after
      // a few seconds instead of leaving the app on "Loading…".
      const { data } = await api.get('/auth/me', { timeout: ME_TIMEOUT_MS });
      if (data.success) {
        apply(data);
        cacheSession({ user: data.user, business: data.business, parentCompany: data.parentCompany, billing: data.billing, legal: data.legal });
        // Wipe + re-bootstrap if the local DB belonged to a different account.
        await ensureAccount(accountKeyOf(data.user, data.business));
        // Nothing syncs until a temporary password has been replaced (the
        // server refuses it anyway).
        if (!data.user.must_change_password) SyncManager.start();
      }
    } catch (err) {
      // Only a real "not signed in" answer ends the session. A network error
      // (offline, switching apps on a weak signal) must NOT log the user out —
      // fall back to the last known session instead.
      if (err.response?.status === 401) {
        clearSession();
      } else {
        const cached = cachedSession();
        if (cached) { apply(cached); SyncManager.start(); }
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadUser(); }, [loadUser]);

  // Refresh session when the tab regains focus so plan changes made by admin
  // (e.g. approving an upgrade) are picked up without requiring a re-login.
  useEffect(() => {
    const onVisible = () => { if (document.visibilityState === 'visible') loadUser(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [loadUser]);

  // Any request answered 401 (expired / revoked session) signs out.
  useEffect(() => {
    const onExpired = () => clearSession();
    window.addEventListener('sb:session-expired', onExpired);
    return () => window.removeEventListener('sb:session-expired', onExpired);
  }, []);

  // The server has already set the session cookie on the sign-in response.
  const login = async (userData, businessData, parentCompanyData, billingData, legalData) => {
    localStorage.setItem(SIGNED_IN, '1');
    apply({ user: userData, business: businessData, parentCompany: parentCompanyData, billing: billingData, legal: legalData });
    cacheSession({ user: userData, business: businessData, parentCompany: parentCompanyData, billing: billingData, legal: legalData });
    // Wipe any previous account's offline data before the new account syncs.
    await ensureAccount(accountKeyOf(userData, businessData));
    if (!userData?.must_change_password) SyncManager.start();
  };

  // Sends anything still queued first. If records still haven't reached the
  // server, returns { pending } without logging out unless `force` is set —
  // the caller asks the user. Logging out then wipes the device's copy
  // INCLUDING the outbox, so the next person to sign in on this phone can't
  // push the previous user's queued records under their own name.
  const logout = async ({ force = false } = {}) => {
    const pending = await SyncManager.flush();
    if (pending > 0 && !force) return { pending };
    SyncManager.stop();
    try { await api.post('/auth/logout'); } catch { /* ignore */ }
    clearSession();
    await resetLocalData().catch(() => {});
    return { pending: 0 };
  };

  const updateBusiness      = (patch) => setBusiness((b) => ({ ...b, ...patch }));
  const updateParentCompany = (patch) => setParentCompany((p) => ({ ...p, ...patch }));
  const updateUser = (patch) => {
    setUser((u) => (u ? { ...u, ...patch } : u));
    // Temporary password just replaced → start syncing now.
    if (patch.must_change_password === false) SyncManager.start();
  };

  // Mark the first-run onboarding as seen. Flip local state immediately so the
  // overlay closes without waiting on the network (offline-first); the request
  // is fire-and-forget and idempotent server-side.
  const completeOnboarding = useCallback(() => {
    setUser((u) => (u ? { ...u, onboarded: true } : u));
    api.post('/auth/complete-onboarding').catch(() => { /* idempotent; safe to re-run if it reappears */ });
  }, []);

  // Paid-tier entitlements. isPro = any paid plan (mid-tier features: team,
  // financials, sync, exports). isEnterprise = top tier only (multi-business /
  // parent-company consolidation suite).
  const isEnterprise = parentCompany?.plan === 'enterprise';
  const isPro        = parentCompany?.plan === 'pro' || isEnterprise;
  const isOwner   = user?.role === 'owner';
  const isManager = isOwner || user?.role === 'manager';
  // Mirrors the server: owners and managers can do everything in their business.
  const hasPermission = (perm) => isManager || !!(user?.permissions?.[perm]);
  const readOnly = !!billing?.read_only;

  return (
    <AuthContext.Provider value={{
      user, business, parentCompany, billing, legal, setLegal, isPro, isEnterprise, isOwner, isManager, hasPermission,
      readOnly, loading,
      login, logout, reload: loadUser,
      updateBusiness, updateParentCompany, updateUser, completeOnboarding,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
